/* eslint-disable no-magic-numbers */
import {
  FinancialDatasetRequest,
  FinancialDatasetResponseCellMeta,
  ReportTemplateList as ReportTemplatePredict
} from '@futrli-sdk/predict';
import { ColumnInfo } from '@futrli/backend-messages';
import { PERIODS } from '../forecast/detail/performance/components/period-dropdown';
import { TableFiltersWithSort } from '../utils/table-filters/filter-table-types';
import { AccountType, NodeStaticId } from '../forecast/prediction-builder/types';

// interfaces
export interface LayoutData {
  i: string;
  type: CARD_TYPES;
  [CARD_TYPES.WIDGET]?: WidgetData;
  [CARD_TYPES.TEXT]?: TextData;
  [CARD_TYPES.APP_WIDGET]?: AppWidgetData;
  [CARD_TYPES.IMAGE]?: ImageData;
  version?: string;
}
export interface LayoutFinQuery {
  i: string; // cardId + forecastId
  query: FinancialDatasetRequest;
}
export interface FinData {
  periods: string[];
  column_info?: ColumnInfo[];
  columns?: string[];
  fields: Record<string, string[]>;
  field_meta: Record<
    string,
    {
      display_type: VALUE_DISPLAY_OPTIONS;
      variance_analysis: 'positive' | 'negative';
      column_meta: FinancialDatasetResponseCellMeta[];
    }
  >;
  forecastId?: string;
  sub_headings?: string[];
}
export interface WidgetData {
  widgetType: string;
  widgetTitle: string;
  widgetSubTitle: string;
  selectedFinFields: DataField[];
  selectedCalFields: DataField[];
  selectedVariances: DataField[];
  selectedComparisons?: { ref: string; comparisons: string[][] }[];
  selectedDatasets?: string[];
  calendarOptions: CalendarOptions | undefined;
  primaryAxisOptions: AxisOptions | undefined;
  secondaryAxisOptions?: AxisOptions | undefined;
  finFieldsOptions: { ref: string; options: FinFieldOptions | undefined }[];
  headerRow?: TableStyle;
  forecastsRow?: TableStyle;
  normalRow?: TableStyle;
  categoriesRow?: TableStyle;
  groupsRow?: TableStyle;
  accountsRow?: TableStyle;
  assumptionsRow?: TableStyle;
  formulasRow?: TableStyle;
  driverGroupsRow?: TableStyle;
  driversRow?: TableStyle;
  customRow?: { type: GROUPING_OPTIONS; style: TableStyle }[];
  tableFirstColumnWidth?: number;
  tableColumnWidth?: number;
  tableZoom?: number;
  tableShouldPaginate?: boolean;
  collapsedFields?: string[];
  showZeroLine?: boolean;
  showForecastStartLine?: boolean;
  forecastStartLineColors?: { id: string; color: string }[];
  showBarLabels?: boolean;
}
export interface TableStyle {
  fontWeight?: TABLE_FONT_WEIGHTS;
  fontSize?: number;
  color?: string;
  fill?: string;
  fillPast?: string;
  fillFuture?: string;
  fillVariance?: string;
  fillEven?: string;
  fillPastEven?: string;
  fillFutureEven?: string;
  fillVarianceEven?: string;
  strokeColor?: string;
  strokeWidth?: number;
  strokePosition?: string;
}
export interface TextData {
  html: string;
}
export interface AppWidgetData {
  name: APP_WIDGET_NAMES;
  period?: PERIODS;
  widgetFilters?: TableFiltersWithSort;
}
export interface ImageData {
  id: string;
  behavior?: ImageBehavior;
}
export type ClassifierType =
  | 'static'
  | 'group'
  | 'account'
  | 'assumption'
  | 'row-formula'
  | 'template-formula'
  | 'freestyle-driver'
  | 'formula-driver'
  | 'driver-group'
  | 'elimination-group'
  | 'eliminated-account'
  | 'group-by-account-name'
  | 'group-by-org-group-name'
  | '';
export interface DataField {
  ref: string;
  name: string;
  classifier: ClassifierType;
  selected?: boolean;
  not_selectable?: boolean;
  disabled?: boolean;
  children?: DataField[];
  disabledIfMultiDS?: boolean;
  disabledIfOneDS?: boolean;
  locked?: boolean;
  isSecondary?: boolean;
  isDeleted?: boolean;
  type?: 'P&L' | 'CF' | 'BS';
  depth?: number;
  organisation_id?: string;
  has_active_switch_rule?: boolean;
  account_type?: AccountType;
  static_id?: NodeStaticId;
}
export interface PageSetup {
  backgroundColor: string;
  orientation: 'portrait' | 'landscape' | 'dashboard';
  pageSize: PAGE_SIZES;
  margins: Margins[];
  showMargins: boolean;
  zoom: number;
  pageNumbersDisplay: PAGE_NUMBERS_DISPLAY;
}
export interface CalendarOptions {
  type: string;
  startDate: string;
  comparision: COMPARISON_OPTIONS;
  periods: number;
}
export interface AxisOptions {
  scaling: SCALING_OPTIONS;
  rounding?: ROUNDING_OPTIONS;
  min?: number;
  max?: number;
  valueTreatment?: VALUE_TREATMENT;
  unit?: VALUE_DISPLAY_OPTIONS;
  chartType?: WIDGET_TYPES;
}
export interface FinFieldOptions {
  rounding?: ROUNDING_OPTIONS;
  grouping?: GROUPING_OPTIONS[];
  unit?: VALUE_DISPLAY_OPTIONS;
  colors?: string[] | Record<string, string>;
  excludedChildren?: string[];
  includedChildren?: string[]; // when a child is excluded but we want to include a grand child, we add it here.
}
export interface Margins {
  label: string;
  value: number;
}
export interface ReportMappingData {
  line_data_references: Record<string, MappingData>;
  dataset_references: Record<string, MappingData>;
}
export interface MappingData {
  id: string;
  name: string;
  type: string;
  ref?: string;
  data: {
    kind?: string;
    organisation_short_code?: string;
    expression?: string;
    group?: string;
    variance_analysis?: string;
    output_type?: string;
  };
}

export type TableCellFillType =
  | 'fill'
  | 'fillPast'
  | 'fillFuture'
  | 'fillVariance'
  | 'fillEven'
  | 'fillPastEven'
  | 'fillFutureEven'
  | 'fillVarianceEven';
export type RowTypes =
  | 'headerRow'
  | 'forecastsRow'
  | 'normalRow'
  | 'categoriesRow'
  | 'groupsRow'
  | 'accountsRow'
  | 'assumptionsRow'
  | 'formulasRow'
  | 'driverGroupsRow'
  | 'driversRow';

export type ImageBehavior = 'original' | 'fitWidth' | 'fitHeight' | 'stretch';

export type RestIconNameTypes =
  | 'chart-1'
  | 'chart-2'
  | 'chart-3'
  | 'charts-icon'
  | 'colors-icon'
  | 'eye-icon'
  | 'pdf'
  | 'add-folder'
  | 'folder'
  | 'delete-icon'
  | 'pen-icon'
  | 'excel-icon'
  | 'mini-excel-icon';

// enums
export enum CARD_TYPES {
  TEXT = 'text',
  APP_WIDGET = 'app_widget',
  WIDGET = 'widget',
  IMAGE = 'image'
}
export enum WIDGET_TYPES {
  BAR_HORIZONTAL = 'bar-h',
  BAR_VERTICAL = 'bar-v',
  BAR_MULTI_HORIZONTAL = 'bar-multi-h',
  BAR_MULTI_VERTICAL = 'bar-multi-v',
  PIE = 'pie',
  DONUT = 'donut',
  LINE = 'line',
  LINE_HORIZONTAL = 'line-h',
  AREA = 'area',
  TABLE = 'table',
  DATA_CARD = 'data-card'
}
// order of rendering in the svg (like z-index)
export const WidgetTypesOrder = {
  [WIDGET_TYPES.BAR_HORIZONTAL]: 2,
  [WIDGET_TYPES.BAR_VERTICAL]: 2,
  [WIDGET_TYPES.BAR_MULTI_HORIZONTAL]: 2,
  [WIDGET_TYPES.BAR_MULTI_VERTICAL]: 2,
  [WIDGET_TYPES.PIE]: 1,
  [WIDGET_TYPES.DONUT]: 1,
  [WIDGET_TYPES.LINE]: 3,
  [WIDGET_TYPES.LINE_HORIZONTAL]: 3,
  [WIDGET_TYPES.AREA]: 1,
  [WIDGET_TYPES.TABLE]: 1,
  [WIDGET_TYPES.DATA_CARD]: 1
};
export enum PAGE_NUMBERS_DISPLAY {
  NONE = 'none',
  ONE = '1',
  ONE_OF_ONE = '1 of 1',
  PAGE_ONE = 'Page 1',
  PAGE_ONE_OF_ONE = 'Page 1 of 1'
}
export enum APP_WIDGET_NAMES {
  CASH_IN_FROM_INVOICES = 'cash-in-from-invoices',
  CASH_OUT_FROM_BILLS = 'cash-out-from-bills',
  TOP_INVOICES_DUE = 'top-invoices-due',
  TOP_BILLS_OWED = 'top-bills-owed',
  TOP_SALES_ACCOUNTS = 'top-sales-accounts',
  TOP_COST_AND_EXPENSES_ACCOUNTS = 'top-costs-and-expenses-accounts',
  ACCOUNT_BRINGING_MOST_CASH = 'top-cash-in-accounts',
  CUSTOMERS_PAID_MOST = 'customers-who-have-paid-the-most',
  ACCOUNTS_GREATEST_CASH_EXPENDITURE = 'top-cash-out-accounts',
  SUPPLIERS_YOU_PAID_MOST = 'suppliers-you-have-paid-the-most',
  TOP_DEBT_BALANCES = 'top-debt-balances',
  RATIOS = 'ratios',
  CUSTOMERS_AND_SUPPLIERS = 'customers-and-suppliers'
}
export enum VALUE_DISPLAY_OPTIONS {
  CURRENCY = 'currency',
  PERCENTAGE = 'percentage',
  PERCENTAGE_RAW = 'percentage_row', // does not need to be multiplied by 100
  NUMBER = 'number'
}
export enum SCALING_OPTIONS {
  AUTO = 'auto',
  MIN_MAX = 'min_max'
}
export enum ROUNDING_OPTIONS {
  TWO_DECIMALS = 'two_decimals',
  ONE_DECIMAL = 'one_decimal',
  INTEGER = 'integer',
  TENS = 'tens',
  HUNDREDS = 'hundreds',
  THOUSANDS = 'thousands',
  MILLIONS = 'millions'
}
export enum GROUPING_OPTIONS {
  CATEGORIES = 'categories',
  GROUPS = 'groups',
  ACCOUNTS = 'accounts',
  ASSUMPTIONS = 'assumptions',
  FORMULAS = 'formulas',
  DRIVER_GROUPS = 'driverGroups',
  DRIVERS = 'drivers'
}
export enum GROUPING_LEVEL {
  CATEGORIES = 10,
  GROUPS = 8,
  ACCOUNTS = 6,
  DRIVER_GROUPS = 4,
  ASSUMPTIONS = 1,
  FORMULAS = 1,
  DRIVERS = 1
}
export enum VALUE_TREATMENT {
  SUM = 'sum',
  AVERAGE = 'average'
}
export enum PAGE_SIZES {
  A4 = 'A4',
  A5 = 'A5',
  A6 = 'A6'
}
export enum TABLE_FONT_WEIGHTS {
  LIGHTER = 200,
  NORMAL = 400,
  BOLD = 500,
  BOLDER = 800
}
export enum STROKE_BORDER_POSITIONS {
  NONE = 'None',
  BOTTOM = 'Bottom border',
  RIGHT = 'Right border',
  LEFT = 'Left border',
  TOP = 'Top border',
  TOP_BOTTOM = 'Top bottom border',
  ALL_ROUND = 'All round border'
}

export enum PERIOD_OPTIONS {
  THIS_PERIOD = 'this_period',
  LAST_PERIOD = 'last_period',
  OTHER = 'other'
}
export enum COMPARISON_OPTIONS {
  PREVIOUS = 'previous',
  FUTURE = 'future'
}

export type PeriodType = 'daily' | 'monthly' | 'weekly_mon' | 'weekly_sun' | 'quarterly' | 'yearly';

export enum PERIOD_END_OPTIONS {
  CUSTOM_DATES = 'custom_dates',
  MIXED = 'mixed',
  CUSTOM = 'custom',
  YESTERDAY = 'yesterday',
  TODAY = 'today',
  LAST_WEEK_MON = 'last_week_mon',
  THIS_WEEK_MON = 'this_week_mon',
  LAST_WEEK_SUN = 'last_week_sun',
  THIS_WEEK_SUN = 'this_week_sun',
  LAST_MONTH = 'last_month',
  THIS_MONTH = 'this_month',
  LAST_QUARTER = 'last_quarter',
  THIS_QUARTER = 'this_quarter',
  LAST_FIN_YEAR = 'last_year',
  THIS_FIN_YEAR = 'this_year'
}
export interface PeriodEndOption {
  name: string;
  value: PERIOD_END_OPTIONS;
  type: PeriodType;
  typeName: string;
  start: PERIOD_OPTIONS;
}
export type DatasetOptionType = 'financial' | 'calendar' | 'formula' | 'column-formula' | 'dataset';
export type OrientationType = 'portrait' | 'landscape' | 'dashboard';

export interface OnboardingStepText {
  title: string;
  desc?: string;
}

export enum ONBOARDING_STEPS {
  ADD_TEMPLATE = 1,
  ADD_WIDGET = 2,
  SELECT_WIDGET = 3,
  ADD_TEXT_IMAGES = 4,
  SEND_OR_SCHEDULE = 5,
  SEE_HELP = 6
}

export interface OnboardingRouteData {
  cameFromOnboarding: boolean;
}

export enum FIELD_TYPES {
  FIN_STATEMENTS = 'financial-statements',
  CATEGORIES = 'categories',
  GROUPS = 'groups',
  ACCOUNTS = 'accounts',
  PREDICTIONS = 'predictions',
  DRIVERS = 'drivers',
  FORMULAS = 'formulas'
}

export interface LineBarDataColor {
  color: string;
  key: string;
  ref: string;
  forecastId: string;
  name: string;
  type: WIDGET_TYPES;
  isSecondary?: boolean;
}

export interface PieDonutData {
  forecastName: string;
  forecastRef: string;
  data: {
    name: string;
    uuid: string;
    value: number;
    color: string;
  }[];
}

export interface LineBarData {
  colors: LineBarDataColor[];
  data: Record<string, string | number>[];
}

export interface ReportTemplate extends ReportTemplatePredict {
  consolidation_id?: string;
}

export interface DatasetComparisonChild {
  name: string;
  ref: string;
  classifier: string;
  children: never[];
  isGeneric?: boolean;
}
